import { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Loader2, UserPlus, Search, Check } from 'lucide-react';

const ROLE_COLORS = {
  super_admin: 'bg-red-100 text-red-800',
  admin: 'bg-purple-100 text-purple-800',
  product_admin: 'bg-purple-100 text-purple-800',
  product_manager: 'bg-blue-100 text-blue-800',
  product_executive: 'bg-cyan-100 text-cyan-800',
  buyer: 'bg-emerald-100 text-emerald-800',
  manager: 'bg-amber-100 text-amber-800',
  sales: 'bg-orange-100 text-orange-800',
  viewer: 'bg-gray-100 text-gray-800',
};

const getRoleLabel = (roles) => {
  if (!roles || !roles.length) return 'User';
  const r = roles[0];
  return r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

export default function AssignItemsModal({ open, onClose, selectedItems, productManagers, selectedPM, setSelectedPM, onAssign, assigning }) {
  const selectedCount = selectedItems.size;
  const [search, setSearch] = useState('');

  const filteredUsers = useMemo(() => {
    if (!search) return productManagers;
    const q = search.toLowerCase();
    return productManagers.filter(u =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.roles || []).some(r => r.toLowerCase().includes(q)) ||
      (u.profile_name || '').toLowerCase().includes(q)
    );
  }, [productManagers, search]);

  const selectedUser = productManagers.find(u => u.id === selectedPM);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setSearch(''); } onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Assign Items</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Selected Items */}
          <div className="space-y-2">
            <Label>Selected Items</Label>
            <div className="p-3 bg-muted/50 rounded-sm max-h-[120px] overflow-auto">
              <div className="text-sm font-medium mb-2">{selectedCount} item{selectedCount !== 1 ? 's' : ''} selected</div>
              <div className="flex flex-wrap gap-1">
                {Array.from(selectedItems).slice(0, 10).map(sku => (
                  <Badge key={sku} variant="secondary" className="text-xs rounded-sm">{sku}</Badge>
                ))}
                {selectedCount > 10 && (
                  <Badge variant="outline" className="text-xs rounded-sm">+{selectedCount - 10} more</Badge>
                )}
              </div>
            </div>
          </div>

          {/* User Selection */}
          <div className="space-y-2">
            <Label>Assign to User</Label>

            {/* Selected user display */}
            {selectedUser && (
              <div className="flex items-center justify-between p-2 bg-primary/5 border border-primary/20 rounded-sm">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary">
                    {(selectedUser.name || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <span className="text-sm font-medium">{selectedUser.name}</span>
                    <Badge className={`ml-2 text-[10px] rounded-sm ${ROLE_COLORS[selectedUser.roles?.[0]] || ROLE_COLORS.viewer}`}>
                      {selectedUser.profile_name || getRoleLabel(selectedUser.roles)}
                    </Badge>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setSelectedPM('')}>Change</Button>
              </div>
            )}

            {/* Search + dropdown (show when no user selected) */}
            {!selectedUser && (
              <>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search and select a user"
                    className="pl-9 rounded-sm"
                    data-testid="user-search-input"
                    autoComplete="off"
                  />
                </div>
                <ScrollArea className="h-[220px] border rounded-sm">
                  {filteredUsers.length === 0 ? (
                    <div className="p-6 text-center text-sm text-muted-foreground">No users found</div>
                  ) : (
                    filteredUsers.map(user => {
                      const isSelected = user.id === selectedPM;
                      const roleLabel = user.profile_name || getRoleLabel(user.roles);
                      const roleColor = ROLE_COLORS[user.roles?.[0]] || ROLE_COLORS.viewer;
                      return (
                        <div
                          key={user.id}
                          className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/50 border-b last:border-b-0 transition-colors ${isSelected ? 'bg-primary/5' : ''}`}
                          onClick={() => { setSelectedPM(user.id); setSearch(''); }}
                          data-testid={`user-option-${user.id}`}
                        >
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm font-medium shrink-0">
                            {(user.name || '?')[0].toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{user.name || user.email}</div>
                            <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                          </div>
                          <Badge className={`text-[10px] rounded-sm shrink-0 ${roleColor}`}>{roleLabel}</Badge>
                          {isSelected && <Check className="h-4 w-4 text-primary shrink-0" />}
                        </div>
                      );
                    })
                  )}
                </ScrollArea>
              </>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={onAssign} disabled={assigning || !selectedPM} className="rounded-sm" data-testid="confirm-assign-btn">
            {assigning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
            Assign Items
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
